import React, { useState, useEffect } from 'react';
import { Box, Typography, Avatar, IconButton, Menu, MenuItem, Button, Tooltip } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from "react-router-dom";
import baseUrl from '../config';
import axios from 'axios';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import { Card, CardContent } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';

// ===============================
// Styled Components
// ===============================

const StyledCard = styled(motion.div)(({ theme }) => ({
    width: '100%',
    maxWidth: 400,
    borderRadius: '1rem',
    boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.1)',
    backgroundColor: '#fff',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        transform: 'scale(1.02)',
        boxShadow: '0 0.75rem 1.5rem rgba(0, 0, 0, 0.15)',
    },
    [theme.breakpoints.down('sm')]: {
        margin: '1rem auto',
        maxWidth: '95%',
    },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    textAlign: 'left',
    [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        alignItems: 'center',
        textAlign: 'left'
    },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
    width: theme.spacing(10),
    height: theme.spacing(10),
    border: '2px solid #3498db',
    boxShadow: '0 0.25rem 0.5rem rgba(0, 0, 0, 0.1)',
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(0),
}));

const StyledInfoBox = styled(Box)(({ theme }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
}));

const StyledName = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: '1.5rem',
    color: '#2c3e50',
    marginBottom: theme.spacing(0.5),
}));

const StyledDetails = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem',
    color: '#7f8c8d',
    marginBottom: theme.spacing(1),
}));

const StyledCourseInfo = styled(Box)(({ theme }) => ({
    backgroundColor: '#ecf0f1',
    borderRadius: '0.75rem',
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
    boxShadow: '0 0.125rem 0.375rem rgba(0, 0, 0, 0.05)',
    width: '100%'
}));

const StyledCourseTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 'semibold',
    fontSize: '1.1rem',
    color: '#34495e',
    marginBottom: theme.spacing(0.5),
}));

const StyledStats = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-around',
    flexWrap: 'wrap',
    margin: theme.spacing(1, 0),
}));

const StyledStatItem = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    margin: theme.spacing(1, 1),
}));

const StyledStatValue = styled(Typography)(({ theme }) => ({
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#3498db',
}));

const StyledStatLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.8rem',
    color: '#95a5a6',
}));

const StyledActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row', // Changed to row
    alignItems: 'center', // Vertically center items
    marginTop: theme.spacing(2),
    justifyContent: 'flex-end'
}));

const StyledTeacherButton = styled(Button)(({ theme }) => ({
    backgroundColor: '#3498db',
    color: '#fff',
    fontWeight: 'semibold',
    padding: theme.spacing(1, 2),
    borderRadius: '1rem',
    marginRight: theme.spacing(2), // Added right margin
    '&:hover': {
        backgroundColor: '#2980b9',
        boxShadow: '0 0.25rem 0.5rem rgba(0, 0, 0, 0.1)',
    },
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
}));

const StyledSettingsButton = styled(IconButton)(({ theme }) => ({
    color: '#7f8c8d',
    '&:hover': {
        color: '#34495e',
    },
}));

const StyledEditProfileButton = styled(Button)(({ theme }) => ({
    backgroundColor: '#fff',
    color: '#3498db',
    fontWeight: 'semibold',
    padding: theme.spacing(1, 2),
    borderRadius: '1rem',
    border: '2px solid #3498db',
    width: '100%',
    '&:hover': {
        backgroundColor: '#ecf0f1',
        boxShadow: '0 0.25rem 0.5rem rgba(0, 0, 0, 0.1)',
    },
    transition: 'all 0.3s ease'
}));

// ===============================
// Component
// ===============================

const UserProfileCard = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const navigate = useNavigate();
    const [coursesData, setCoursesData] = useState([]);
    const [student, setStudent] = useState({ name: '', id: '', avatar: '', grade: '' });

    // Fetch functions remain the same
    const fetchUpcomingCourses = async () => {
        try {
            const response = await axios.get(`${baseUrl}api/students/enrolled-courses`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            });
            setCoursesData(response.data);
        } catch (error) {
            console.error('Error fetching courses:', error);
        }
    };

    const fetchUserDetails = () => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (user) {
            setStudent({
                name: user.name,
                grade: user.grade
            });
        }
    };

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const { data } = await axios.get(baseUrl + 'api/students/profile', {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                });
                setStudent({
                    name: data.name,
                    id: data.uniqueId,
                    avatar: data.avatar,
                    grade: data.grade
                });
            } catch (error) {
                console.error('Error fetching profile data:', error);
            }
        };
        fetchUpcomingCourses();
        fetchProfile();
        fetchUserDetails();
    }, []);

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        handleClose();
        navigate("/login");
    };
    const handleTeacherProfileNavigation = () => {
        if (coursesData && coursesData[0]?.enrollmentTeacherId) {
            navigate(`/teacher/profile/${coursesData[0].enrollmentTeacherId}`);
        }
    }

    return (
        <StyledCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <StyledCardContent>
                <StyledAvatar src={`${baseUrl}${student.avatar}`} />
                <StyledInfoBox>
                    <StyledName>{student.name}</StyledName>
                    <StyledDetails>Grade: {student.grade} | ID: {student.id}</StyledDetails>
                    {coursesData[0]?.teacherName && (
                        <Typography variant="body2" color="textSecondary">
                            Teacher:
                            <Button
                                variant="text"
                                onClick={handleTeacherProfileNavigation}
                                sx={{
                                    padding: 0,
                                    marginLeft: '0.25rem',
                                    color: '#3498db',
                                    fontWeight: 'semibold',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                {coursesData[0].teacherName}
                            </Button>
                        </Typography>
                    )}
                </StyledInfoBox>
               
            </StyledCardContent>

            <StyledCourseInfo>
                <StyledCourseTitle>{coursesData[0]?.course?.title}</StyledCourseTitle>
                <StyledStats>
                    <StyledStatItem>
                        <StyledStatValue>{coursesData[0]?.bookedClasses || 0}</StyledStatValue>
                        <StyledStatLabel>Total Classes</StyledStatLabel>
                    </StyledStatItem>
                    <StyledStatItem>
                        <StyledStatValue style={{ color: coursesData[0]?.completedClasses > 0 ? '#2ecc71' : '#e74c3c' }}>
                            {coursesData[0]?.completedClasses || 0}
                        </StyledStatValue>
                        <StyledStatLabel>Completed</StyledStatLabel>
                    </StyledStatItem>
                    <StyledStatItem>
                        <StyledStatValue>0</StyledStatValue>
                        <StyledStatLabel>Lost Classes</StyledStatLabel>
                    </StyledStatItem>
                </StyledStats>
            </StyledCourseInfo>
            <Box px={3} pb={3}>
                <StyledEditProfileButton onClick={() => navigate("/profile")}>
                    Edit Profile
                </StyledEditProfileButton>
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        boxShadow: 5,
                        borderRadius: '0.75rem',
                        minWidth: 120,
                    },
                }}
            >
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
        </StyledCard>
    );
};

export default UserProfileCard;

